import React from "react";
import { Box, Button, Chip, Divider, Paper, Typography } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

const ChapterEndCard = ({
  hasPrev,
  hasNext,
  prevChapter,
  nextChapter,
  nextChapterSubtitle = "",
  onPrev,
  onNext,
  onOpenCatalog,
  isCompleted = false,
  onToggleCompleted,
  highlightsCount = 0,
  notesCount = 0,
  sharedNotes = [],
  edition,
  isMobile = false,
}) => {
  const ui = edition?.ui || {};
  const nextTitle = nextChapter ? nextChapter.title.replace(/^#\s*/, "") : "";
  const prevTitle = prevChapter ? prevChapter.title.replace(/^#\s*/, "") : "";

  return (
    <Paper
      elevation={0}
      data-testid="chapter-end-card"
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.02)"
            : "rgba(0, 0, 0, 0.015)",
      }}
    >
      {/* End of chapter divider */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography
          variant="overline"
          sx={{
            fontWeight: 800,
            letterSpacing: "0.14em",
            color: "text.secondary",
            fontSize: "0.78rem",
          }}
        >
          {ui.chapterEndDivider || "— 本章完 —"}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            mt: 0.25,
          }}
        >
          {ui.chapterStatsSummary
            ? ui.chapterStatsSummary(highlightsCount, notesCount)
            : `本章标记：高亮 ${highlightsCount} 处 · 笔记 ${notesCount} 条`}
        </Typography>
      </Box>

      {/* Mark chapter read button */}
      {onToggleCompleted && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
          <Button
            size="small"
            variant={isCompleted ? "contained" : "outlined"}
            color={isCompleted ? "success" : "primary"}
            startIcon={
              isCompleted ? (
                <CheckCircleIcon fontSize="small" />
              ) : (
                <RadioButtonUncheckedIcon fontSize="small" />
              )
            }
            onClick={onToggleCompleted}
            sx={{
              borderRadius: 999,
              px: 2.5,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            {isCompleted
              ? ui.chapterMarkedRead || "本章已读完"
              : ui.markChapterRead || "标记本章已读"}
          </Button>
        </Box>
      )}

      {/* Community approved notes preview ("本章说") */}
      {Array.isArray(sharedNotes) && sharedNotes.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}
          >
            {ui.chapterSharedNotesTitle || "本章分享（已审核）"}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {sharedNotes.slice(0, 3).map((note) => (
              <Box
                key={note.id}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {note.userDisplayName || "弟兄姊妹"}
                  </Typography>
                  {note.verseRange && (
                    <Chip
                      label={`第 ${note.verseRange} 节`}
                      size="small"
                      sx={{ height: 18, fontSize: "0.68rem" }}
                    />
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.82rem",
                    color: "text.primary",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {note.content || note.title}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Large 3-button bar: Prev / Catalog / Next */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1.4fr" },
          gap: 1.5,
          alignItems: "stretch",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          onClick={onPrev}
          disabled={!hasPrev}
          fullWidth={isMobile}
          sx={{
            py: 1.2,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: "none",
            justifyContent: "flex-start",
          }}
        >
          <Box sx={{ textAlign: "left", minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", lineHeight: 1.1 }}
            >
              {ui.prevChapter || "上一章"}
            </Typography>
            {prevTitle && (
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, noWrap: true }}
              >
                {prevTitle}
              </Typography>
            )}
          </Box>
        </Button>

        <Button
          variant="outlined"
          color="primary"
          startIcon={<MenuBookIcon />}
          onClick={onOpenCatalog}
          fullWidth={isMobile}
          sx={{
            px: 2.5,
            py: 1.2,
            borderRadius: 2,
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          {ui.catalog || "目录"}
        </Button>

        <Button
          variant="contained"
          color="primary"
          endIcon={<NavigateNextIcon />}
          onClick={onNext}
          disabled={!hasNext}
          fullWidth={isMobile}
          sx={{
            py: 1.2,
            borderRadius: 2,
            fontWeight: 700,
            textTransform: "none",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ textAlign: "left", minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ display: "block", opacity: 0.85, lineHeight: 1.1 }}
            >
              {ui.nextChapter || "下一章"}
            </Typography>
            {nextTitle && (
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, noWrap: true }}
              >
                {nextTitle}
                {nextChapterSubtitle ? ` · ${nextChapterSubtitle}` : ""}
              </Typography>
            )}
          </Box>
        </Button>
      </Box>
    </Paper>
  );
};

export default ChapterEndCard;
